/**
 * AssetsPanel Component
 * Manages text and image assets for an ad group or asset group
 *
 * This panel provides asset management functionality within the EntityCard context.
 * It reuses the TextAssetsSection and ImageAssetsSection components from AssetManagement.
 */

import React, { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { stripEnumPrefix } from '@/utils/formatEnum';
import type { Campaign, AdGroup, AssetGroup } from '@/types/campaign';
import type { RDACacheEntry } from '@/hooks/useRDACache';
import { TextAssetsSection } from '@/components/features/AssetManagement/components/TextAssetsSection';
import { ImageAssetsSection } from '@/components/features/AssetManagement/components/ImageAssetsSection';
import { AssetTypeSelector } from '@/components/features/AssetManagement/components/AssetTypeSelector';
import type { TextAssetType, AssetTab } from '@/components/features/AssetManagement/types';
import { getAvailableAssetLevels } from '@/components/features/AssetManagement/utils/assetConfig';

interface AssetsPanelProps {
  entity: AdGroup | AssetGroup;
  entityType: 'ad_group' | 'asset_group';
  campaign: Campaign;
  selectedLandingPages: Map<string, boolean>;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
}

export function AssetsPanel({
  entity,
  entityType,
  campaign,
  selectedLandingPages,
}: AssetsPanelProps) {
  const { memberUuid } = useAuth();
  const queryClient = useQueryClient();

  // Normalize campaign type
  const campaignType = stripEnumPrefix(campaign.campaign_type || '');

  // Get entity ID
  const entityId = entityType === 'asset_group'
    ? (entity as AssetGroup).asset_group_id
    : (entity as AdGroup).ad_group_id || (entity as AdGroup).ads_group_id || '';

  // Get available asset levels to determine supported asset types
  const availableLevels = useMemo(
    () => getAvailableAssetLevels(
      campaignType,
      campaign.campaign_id,
      campaign.asset_group_list,
      campaign.ads_group_list
    ),
    [campaignType, campaign.campaign_id, campaign.asset_group_list, campaign.ads_group_list]
  );

  // Find the current level config to get supported text assets
  const currentLevelConfig = useMemo(
    () => availableLevels.find((l) => l.id === entityId),
    [availableLevels, entityId]
  );

  // Get supported text assets for this entity
  const supportedTextAssets = useMemo(() => {
    if (!currentLevelConfig) return [];
    return currentLevelConfig.supportedTextAssets;
  }, [currentLevelConfig]);

  // Get supported image types for this entity
  const supportedImageTypes = useMemo(() => {
    if (!currentLevelConfig) return [];
    return currentLevelConfig.supportedImageTypes || [];
  }, [currentLevelConfig]);

  const supportsImages = supportedImageTypes.length > 0;

  // Selected asset type
  const [selectedAssetType, setSelectedAssetType] = useState<TextAssetType>(
    supportedTextAssets.length > 0 ? supportedTextAssets[0] : 'description'
  );

  // Active tab (text or images)
  const [activeTab, setActiveTab] = useState<AssetTab>('text');

  // Update selected type when supported assets change
  React.useEffect(() => {
    if (supportedTextAssets.length > 0 && !supportedTextAssets.includes(selectedAssetType)) {
      setSelectedAssetType(supportedTextAssets[0]);
    }
  }, [supportedTextAssets, selectedAssetType]);

  // Get entity type for TextAssetsSection
  const assetEntityType = entityType === 'asset_group' ? 'asset_group' : 'ad_group';

  if (!memberUuid) {
    return (
      <div className="p-4">
        <div className="text-center py-6 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-yellow-800">Authentication required to manage assets.</p>
        </div>
      </div>
    );
  }

  if (supportedTextAssets.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">No text assets available for this entity type</p>
          <p className="text-sm text-gray-500 mt-1">
            {campaignType.includes('DEMAND_GEN')
              ? 'Demand Gen assets are managed directly in ads.'
              : campaignType.includes('DISPLAY')
              ? 'Display campaign assets are managed in Responsive Display Ads.'
              : 'This entity type does not support standalone text assets.'}
          </p>
        </div>
      </div>
    );
  }

  // Get entity name for ImageAssetsSection
  const entityName = entityType === 'asset_group'
    ? (entity as AssetGroup).asset_group_name
    : (entity as AdGroup).ad_group_name || (entity as AdGroup).ads_group_name;

  return (
    <div className="p-4">
      {/* Tab Selection (only when entity supports images) */}
      {supportsImages && (
        <div className="mb-4">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('text')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'text'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Text Assets
              </button>
              <button
                onClick={() => setActiveTab('images')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'images'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Images
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Text Assets Tab */}
      {(activeTab === 'text' || !supportsImages) && (
        <>
          {/* Asset Type Selector */}
          {supportedTextAssets.length > 1 && (
            <div className="mb-4">
              <AssetTypeSelector
                assetTypes={supportedTextAssets}
                selectedType={selectedAssetType}
                onChange={setSelectedAssetType}
              />
            </div>
          )}

          {/* Text Assets Section */}
          <TextAssetsSection
            campaignId={campaign.campaign_id}
            campaignType={campaignType}
            googleAdsAccountId={campaign.google_ads_account_id || ''}
            entityType={assetEntityType}
            entityId={entityId}
            assetType={selectedAssetType}
            memberUuid={memberUuid}
            campaign={campaign}
            selectedLandingPages={selectedLandingPages}
            onAssetsUpdated={() => {
              queryClient.invalidateQueries({ queryKey: ['campaign'] });
            }}
          />
        </>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && supportsImages && (
        <ImageAssetsSection
          memberUuid={memberUuid}
          entityType={assetEntityType}
          entityId={entityId}
          entityName={entityName}
          customerId={campaign.google_ads_account_id}
          campaignType={campaignType}
          campaignId={campaign.campaign_id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['campaign'] });
          }}
        />
      )}
    </div>
  );
}

export default AssetsPanel;
